<template>
  <div
    :id="`meeting-${meeting.id}`"
    ref="meetingCard"
    class="
      tw-border
      tw-bg-sf-snow-lighter
      tw-border-sf-neutral-lighter-2
      tw-shadow-sm
      tw-rounded-lg
    "
    data-testid="past-meeting-card"
  >
    <header
      class="
        tw-flex
        tw-h-[60px]
        tw-items-center
        tw-justify-between
        tw-bg-sf-snow-light
        tw-rounded-t-lg
        tw-px-6
      "
      :class="{
        'tw-rounded-b-lg': !meeting.summary,
      }"
    >
      <div class="tw-flex tw-items-center tw-gap-4">
        <span
          class="tw-flex tw-items-center tw-cursor-pointer tw-w-8 tw-h-8"
          data-testid="expand-meeting-button"
          :class="{ 'tw-cursor-not-allowed tw-opacity-50': hasPendingProposal }"
          @click="hasPendingProposal ? null : (isExpanded = !isExpanded)"
        >
          <i
            :class="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
            class="fa tw-text-sf-neutral-light-2 tw-mx-auto"
          ></i>
        </span>
        <span
          v-if="meeting.title"
          class="tw-font-medium tw-text-base tw-text-sf-neutral-dark-2"
        >
          {{ meeting.title }}
        </span>
        <span class="tw-flex tw-items-center tw-gap-2">
          <i class="fa fa-calendar tw-text-sf-neutral-light-2"></i>
          <span class="tw-text-sm tw-text-sf-neutral-dark-1">
            {{ meeting.completedAt }}
          </span>
        </span>
      </div>
      <div class="tw-flex tw-items-center">
        <template v-if="!(hasPendingProposal || hasProcessingProposal)">
          <SfButtonDs
            v-if="!exportInProgress"
            variant="tertiary"
            data-heap="talking-points-export-past"
            @click="generateExportHandler"
          >
            <i class="fa fa-download"></i>
            <span>{{ copy.exportButton }}</span>
          </SfButtonDs>
          <div v-else class="tw-flex tw-items-center tw-gap-2 tw-p-1 tw-pl-2">
            <span class="sf-spinner"></span>
            <span>{{ copy.generatingExport }}</span>
          </div>
        </template>
        <SfButtonDs
          variant="tertiary"
          icon-only
          icon-before="trash"
          :aria-label="copy.deleteMeeting"
          data-testid="delete-meeting-button"
          @click="openDeleteMeetingModal(meeting)"
        />
        <MeetingProposalActions
          v-if="hasPendingProposal"
          :finalizing="isFinalizing"
          @accept="finalize(proposalStore.acceptProposal)"
          @reject="finalize(proposalStore.rejectProposal)"
        />
      </div>
    </header>
    <div
      v-if="isExpanded || meeting.summary"
      class="tw-flex tw-flex-col tw-gap-4 tw-px-8 tw-py-4"
    >
      <div
        v-if="!isExpanded && meeting.summary"
        class="
          tw-bg-slate-50
          tw-rounded-lg
          tw-p-6
          tw-pt-4
          tw-text-sf-neutral-darker-1
          tw-flex
          tw-flex-col
          tw-gap-4
        "
      >
        <div v-dompurify-html="htmlSummary[meeting.id]"></div>
      </div>

      <div v-if="isExpanded" class="tw-flex tw-flex-col tw-gap-4 tw-relative">
        <MeetingProcessingLoader
          v-if="showsLoader"
          :processing="hasProcessingProposal ? 'proposal' : 'summary'"
        />
        <template v-else-if="loading">
          <RemarkSkeleton v-for="i in 2" :key="i" />
        </template>
        <MeetingProposal v-else-if="showsProposal" :meeting="meeting" />
        <MeetingDetails v-else :meeting="meeting" />

        <MeetingProcessingLoader
          v-if="showsOverlay"
          processing="summary"
          overlay
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, onMounted, useTemplateRef, watch, nextTick } from 'vue'

import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'

import useI18n from '@app/shared/vue/composables/use-I18n'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import { getHeaderOffset } from '@app/application/behaviors/fixed-table-headers/utils'
import { isUsingNewLayout } from '@app/adapters/silverfin'

import useExport from '@app/application/client_meetings/screens/composables/use-export'
import type { Remark } from '@app/application/communication/types'
import type { Meeting } from '@app/application/client_meetings/types'
import type { Meeting as PastMeeting } from '@app/application/client_meetings/api/past'
import { ProposalStatus } from '@app/application/client_meetings/types'
import MeetingProcessingLoader from '@app/application/client_meetings/screens/past/components/meeting-processing-loader.vue'
import MeetingProposal from '@app/application/client_meetings/screens/past/components/meeting-proposal/meeting-proposal.vue'
import MeetingProposalActions from '@app/application/client_meetings/screens/past/components/meeting-proposal/meeting-proposal-actions.vue'
import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import { usePastMeetingsStore } from '@app/application/client_meetings/screens/past/stores/past-meetings'
import MeetingDetails from '@app/application/client_meetings/screens/past/components/meeting-details.vue'

const store = useStore()

const I18n = useI18n()
const copy = {
  exportButton: I18n.t('client_meetings.exports.download_processed'),
  generatingExport: I18n.t('client_meetings.exports.generating'),
  deleteMeeting: I18n.t('client_meetings.delete_meeting_modal.delete_button'),
  finalizeError: I18n.t('common.error.try_again'),
}

interface Props {
  meeting: Meeting
  latestMeeting: boolean
}

const props = defineProps<Props>()

const proposalStore = useProposalDetailsStore()
const { statusByProposalId } = storeToRefs(proposalStore)
const pastMeetingsStore = usePastMeetingsStore()
const { htmlSummary } = storeToRefs(pastMeetingsStore)

const proposalStatus = computed(() =>
  props.meeting.proposalId
    ? statusByProposalId.value?.[props.meeting.proposalId]
    : undefined
)

const isExpanded = ref(props.latestMeeting)
const meetingCardRef = useTemplateRef('meetingCard')
const usingNewLayout = isUsingNewLayout()

const remarkIds = computed(() => {
  const remarks = Object.values(
    store.state.remarks.remarks as Record<string, Remark>
  )

  return remarks
    .filter((remark) => remark.clientMeetingId === props.meeting.id)
    .map((remark) => remark.id)
})

const empty = computed(() => remarkIds.value.length === 0)

const loading = computed(
  () => empty.value && store.getters.fetchingCommunicationsInProgress
)

const hasPendingProposal = computed(() => {
  return proposalStatus.value === ProposalStatus.Pending
})

// Check if last proposal is still processing
const hasProcessingProposal = computed(() => {
  return proposalStatus.value === ProposalStatus.Generating
})

const isFinalizing = ref(false)

async function finalize(
  action: (proposalId: number) => Promise<PastMeeting | null>
): Promise<void> {
  const proposalId = props.meeting.proposalId
  if (!proposalId || isFinalizing.value) return

  isFinalizing.value = true

  try {
    const meeting = await action(proposalId)
    if (meeting) await pastMeetingsStore.setPastMeeting(meeting)
  } catch {
    alert(copy.finalizeError)
  } finally {
    isFinalizing.value = false
  }
}

const isProcessing = computed(() => {
  return isFinalizing.value || props.meeting.summaryGenerating
})

// The card has nothing of its own to show yet, so the loader stands in for the
// content instead of the overlay dimming it.
const showsLoader = computed(() => {
  return (
    hasProcessingProposal.value ||
    (props.meeting.summaryGenerating && !props.meeting.proposalId)
  )
})

// The proposal stays on screen while its summary generates, so the processed
// meeting is revealed in a single update.
const showsProposal = computed(() => {
  return hasPendingProposal.value || isProcessing.value
})

const showsOverlay = computed(() => {
  return isProcessing.value && !showsLoader.value
})

const includeHashRemark = (id: string) => {
  if (id.startsWith('#remark-')) {
    const remarkId = Number(id.replace('#remark-', ''))
    return remarkIds.value.some((id: number) => id === remarkId)
  }
  return false
}

watch(remarkIds, () => {
  const elementId = window.location.hash
  if (includeHashRemark(elementId)) {
    isExpanded.value = true
  }
})

onMounted(() => {
  if (
    window.location.hash !== '' &&
    window.location.hash !== `#meeting-${props.meeting.id}`
  ) {
    isExpanded.value = false
  }

  if (
    hasProcessingProposal.value ||
    hasPendingProposal.value ||
    window.location.hash === `#meeting-${props.meeting.id}`
  ) {
    isExpanded.value = true
  }
})

watch(isExpanded, (newValue, oldValue) => {
  nextTick(() => {
    if (newValue && newValue !== oldValue && meetingCardRef.value) {
      if (usingNewLayout) {
        meetingCardRef.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        window.scrollTo({
          top:
            meetingCardRef.value.getBoundingClientRect().top +
            window.scrollY -
            getHeaderOffset(),
          behavior: 'smooth',
        })
      }
    }
  })
})

function openDeleteMeetingModal(meeting: Meeting) {
  window.modalCitizen.openModalPortal('delete-meeting-modal', { meeting })
}

const { exportInProgress, generateExport } = useExport({
  meetingId: props.meeting.id,
})

async function generateExportHandler() {
  const response = await generateExport()
  if (response && response.url) {
    window.open(response.url, '_blank')
  }
}
</script>
